// Monthly recurring revenue, counted only from live Stripe subscriptions.
//
// REVENUE MEANS A LIVE SUBSCRIPTION, NOT A TIER. Hand-granted tiers pay
// nothing, so they are never counted (`billing.paying === true` only; absent
// billing is never paying).
//
// WHAT EACH SUBSCRIBER PAYS. The growth change raised list prices, and existing
// subscribers keep their old price until they change plan (grandfathered). So a
// list price is no longer a safe stand-in for what someone pays: a grandfathered
// $9.95 Pro subscriber multiplied by the new $12 would overstate revenue.
//   - When the server reports the charged amount (billing.unitAmountCents and
//     interval), that is used, and the row counts as "exact".
//   - Otherwise the list price is used, and the row counts as an "estimate".
// Pages show how many rows are estimates, so the figure says what it is.
//
// SEATS ONLY WHERE THE PLAN IS PER-SEAT. billableSeats is derived from org
// membership, not Stripe's quantity; Stripe's quantity is preferred when the
// server reports it.

import type { AdminUser } from './domain'
import { tierSpec } from './tiers'

export interface MrrLine {
  userId: string
  tier: string
  monthlyUsd: number
  exact: boolean
  interval: 'month' | 'year' | null
  grandfathered: boolean | null
}

export interface MrrSummary {
  totalUsd: number
  paying: number
  exact: number
  estimated: number
  /** Paying accounts whose tier has no list price (Enterprise, or an unknown
   *  tier) and no reported amount: excluded from the total, and counted here. */
  unpriced: number
  lines: MrrLine[]
}

export function isPaying(u: AdminUser): boolean {
  return u.status === 'active' && u.billing?.paying === true
}

/** A plan paid from the referral credit bank (growth spec 4b): no card, no
 *  Stripe revenue, and not a hand grant either. */
export function isCreditFunded(u: AdminUser): boolean {
  return u.status === 'active' && u.billing?.paying !== true && !!u.referrals?.planFundedByCredit
}

/** A non-free tier set by hand: no subscription and no bank credit pays for it. */
export function isGranted(u: AdminUser): boolean {
  return u.status === 'active' && u.tier !== 'free' && u.billing?.paying !== true && !isCreditFunded(u)
}

export function mrrLine(u: AdminUser): MrrLine | null {
  const b = u.billing
  if (!b || !isPaying(u)) return null
  const tier = b.plan ?? u.tier
  const interval = b.interval ?? null
  const grandfathered = typeof b.grandfathered === 'boolean' ? b.grandfathered : null
  if (typeof b.unitAmountCents === 'number' && interval) {
    const qty = Math.max(1, b.quantity ?? 1)
    const perInterval = (b.unitAmountCents / 100) * qty
    return {
      userId: u.id,
      tier,
      monthlyUsd: interval === 'year' ? perInterval / 12 : perInterval,
      exact: true,
      interval,
      grandfathered
    }
  }
  const spec = tierSpec(tier)
  const monthly = interval === 'year' && spec?.priceYearly != null ? spec.priceYearly / 12 : spec?.priceMonthly
  if (monthly == null) return null
  const seats = spec?.unit === 'seat' ? Math.max(1, b.quantity ?? b.billableSeats ?? 1) : 1
  return { userId: u.id, tier, monthlyUsd: monthly * seats, exact: false, interval, grandfathered }
}

export function summariseMrr(users: AdminUser[]): MrrSummary {
  const out: MrrSummary = { totalUsd: 0, paying: 0, exact: 0, estimated: 0, unpriced: 0, lines: [] }
  for (const u of users) {
    if (!isPaying(u)) continue
    out.paying += 1
    const line = mrrLine(u)
    if (!line) {
      out.unpriced += 1
      continue
    }
    out.lines.push(line)
    out.totalUsd += line.monthlyUsd
    if (line.exact) out.exact += 1
    else out.estimated += 1
  }
  out.totalUsd = Math.round(out.totalUsd * 100) / 100
  return out
}

/** One line under an MRR figure saying how it was computed. */
export function mrrBasis(s: MrrSummary): string {
  if (s.paying === 0) return 'No live subscriptions'
  const parts: string[] = []
  if (s.exact) parts.push(`${s.exact} at charged price`)
  if (s.estimated) parts.push(`${s.estimated} estimated at list price`)
  if (s.unpriced) parts.push(`${s.unpriced} custom-priced, not counted`)
  return parts.join(' · ')
}
